import { useEffect, useMemo, useState } from "react";
import { bool, string, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { MAX_WORK_TYPE_LENGTH, PriceFormats } from "@repo/utils";
import { settingsRates } from "@paths";
import { DEFAULT_WT_ROLES, lastResourceNameUsage, simplifyTextString } from "@utils";
import {
  useEstimateStructure,
  TeamMembersTag,
  useEstimateEditorStore,
  useEstimateEditorSettings,
} from "@tools";
import { useEditorWebsocket, useSearchableList } from "@hooks";
import { DifferentSalesRateActionAlert } from "@tools/EstimateEditor/dialogs/DifferentSalesRateActionAlert";
import {
  TextClearInput,
  SearchableListContent,
  MenuButton,
  Checkbox,
} from "@components";
import Grid from "@material-ui/core/Grid";
import { ListItem } from "./ListItem";
import { Pencil } from "@assets";

export const WorkTypeResourceSelect = observer(({
  resource,
  noMinWidth,
  displayTeamMembers,
  timelineAutofocus,
  navigationKey,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const { hasSellerPrivileges, useProfitability } = useEstimateEditorSettings();
  const socket = useEditorWebsocket()

  const { priceFormat } = structure.settings;
  const { id, name, autoFocus, autoFocusTimeline, teamMembers, workTypeId } = resource;
  const { importRoleCostRates, importRoleSalesRates, defaultCostCurrency, defaultSalesCurrency } = editorStore;

  const [ roles, setRoles ] = useState([]);
  const [ newRoleData, setNewRoleData ] = useState(null);

  const newlyCreatedRole = useMemo(() => (
    timelineAutofocus ? autoFocusTimeline : autoFocus
  ), []);

  const hasSalesRates = useMemo(() => Boolean(
    roles?.find(r => r.salesRate)
  ), [ roles ]);

  const rateTextModifier = useMemo(() => (
    structure.settings?.timeModifier === 1 ? "hours" : "days"
  ), [ structure.settings?.timeModifier ]);

  useEffect(() => {
    setRoles(
      editorStore.workTypes.find(wT => wT.id === workTypeId)?.roles
      || DEFAULT_WT_ROLES.map(roleName => ({ roleName }))
    )
  }, [ editorStore.workTypes ]);

  const handleSalesRateImport = () => {
    editorStore.switchRoleSalesRatesImport()
  };

  const handleCostRateImport = () => {
    editorStore.switchRoleCostRatesImport()
  };

  const handleRateAction = (newRole) => {
    updateRole(newRole)
    setNewRoleData(null);
  }

  const updateRole = (newRole) => {
    if(!newlyCreatedRole)
      structure.historyManager.startGroup();
    
    const path = resource.treePath.join("/");
    const timelinePath = "t" + path;
    const oldName = resource.name;
    const { id: systemId, roleName, salesRate, costRate } = newRole;
    
    const lastNameUsage = lastResourceNameUsage(
      resource.parent?.resources,
      roleName
    );
    
    const newName = lastNameUsage >= 0
      ? `${roleName} ${lastNameUsage + 1}`
      : roleName;

    if(salesRate && importRoleSalesRates)
      resource.setRate(salesRate);
    if(costRate && importRoleCostRates)
      resource.setCompanyUnitCost(costRate);
    
    resource.changeName(newName, systemId);
    
    if(newlyCreatedRole)
      structure.addSectionsResource(workTypeId, id);
    else {
      socket?.requestCommentPathElementRename(path, newName);
      socket?.requestCommentPathElementRename(timelinePath, newName);
      structure.historyManager.addCommentNamePatch(path, oldName, newName);
      structure.historyManager.addCommentNamePatch(timelinePath, oldName, newName);
    }
    structure.historyManager.stopGroup();
  };
  
  const handleEmptyName = () => {
    if(newRoleData)
      setNewRoleData(null);
    if(resource.name)
      return;
    
    resource.removeSelf();
  }

  const handleNewRole = async ({ name: newName }) => {
    if(!newName && !resource.name) {
      handleEmptyName();
      return;
    }
    
    let newRole = roles.find(({ roleName }) => (
      simplifyTextString(roleName) === simplifyTextString(newName)
    ));
    
    if(newRole)
      setNewRoleData(newRole);
    else
      updateRole({ roleName: newName });
  };
  
  const selectExistingRole = (role) => async () => {
    looseFocus();
    setNewRoleData(role);
  };

  const {
    containerProps,
    inputProps,
    hadAutofocus,
    hadInteraction,
    looseFocus
  } = useSearchableList({
    errorOnEmpty: true,
    defaultValue: name,
    autoFocus: timelineAutofocus ? autoFocusTimeline : autoFocus,
    onAutoFocus: timelineAutofocus
      ? resource.removeAutofocusTimeline
      : resource.removeAutofocus,
    onBlur: handleEmptyName,
    onChange: handleNewRole
  });
  
  const availableRoles = useMemo(() => {
    if(!hadInteraction)
      return roles;
    
    return roles.filter(({ roleName }) => {
      if(roleName === inputProps.value)
        return true;
      
      return simplifyTextString(roleName).indexOf(
        simplifyTextString(inputProps.value)
      ) >= 0;
    })
  }, [ roles, inputProps.value, hadInteraction ]);

  return (
    <>
      <TextClearInput
        {...inputProps}
        acceptOnClickAway
        confirmOnEnter
        placeholder={
          inputProps.focused
            ? t("views.editor.dialogs.resources.focused")
            : t("views.editor.dialogs.resources.placeholder")
        }
        noMinWidth={noMinWidth}
        containerClass="name"
        className="w-max -m-0-5"
        maxLength={MAX_WORK_TYPE_LENGTH}
        preventClickAwayOnElement="#resourceSelector"
        inputClassName="input-name"
        navigationKey={navigationKey}
        noBlurFromEnterKey={hadAutofocus}
      />
      {
        displayTeamMembers &&
        <TeamMembersTag teamMembers={teamMembers} />
      }
      {
        Boolean(newRoleData) &&
        <DifferentSalesRateActionAlert
          onAccept={handleRateAction}
          onCancel={handleEmptyName}
          newElementData={newRoleData}
        />
      }
      <SearchableListContent {...containerProps} id="resourceSelector">
        <Grid item container spacing={0} alignItems="center" className="py-1-5 pl-4 pr-2">
          <Grid item xs={4} className="text-xs text-secondary">
            {t("Role")}
          </Grid>
          <Grid item xs={3}>
            {
              hasSellerPrivileges && useProfitability &&
              <Checkbox
                size="small"
                name="lockOption"
                colorSecondary
                checked={importRoleCostRates}
                onChange={handleCostRateImport}
                label={`${t(`views.editor.summary_table.cost_${rateTextModifier}`)} (${defaultCostCurrency})`}
              />
            }
          </Grid>
          <Grid item xs={3}>
            {
              hasSellerPrivileges && (useProfitability || hasSalesRates) &&
              <Checkbox
                size="small"
                name="lockOption"
                colorSecondary
                checked={importRoleSalesRates}
                onChange={handleSalesRateImport}
                label={`${t(`views.editor.summary_table.rate_${rateTextModifier}`)} (${defaultSalesCurrency})`}
              />
            }
          </Grid>
        </Grid>
        {
          availableRoles.map((role) => (
            <ListItem
              key={`role_${role.id || role.roleName}`}
              onClick={selectExistingRole(role)}
              role={role}
              americanPriceFormat={priceFormat === PriceFormats.AMERICAN}
              showSalesRates={hasSalesRates}
            />
          ))
        }
        <Grid item container justifyContent="space-between" alignItems="center">
          <MenuButton
            icon={<Pencil />}
            color="primary"
            className="w-full h-max py-1-5 pl-4 pr-2"
            to={settingsRates}
            target="_blank"
          >
            {t("Manage roles")}
          </MenuButton>
        </Grid>
      </SearchableListContent>
    </>
  );
});

WorkTypeResourceSelect.propTypes = {
  resource: object.isRequired,
  displayTeamMembers: bool,
  noMinWidth: bool,
  navigationKey: string.isRequired,
};
